import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageService from "../../../services/KangarooServices/PageService";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { formatDateTime } from "../../../Components/Common/FormatDate";
import {
  Edit3,
  Calendar,
  Tag,
  FileText,
  Globe,
  Image as ImageIcon,
  User,
  Clock,
} from "lucide-react";
import {
  imageBaseApi,
  offshoreUrl,
  onshoreUrl,
} from "../../../utilis/imageHelper";
import toast from "react-hot-toast";
import { RiFileCopyLine } from "react-icons/ri";

const PageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detailData, setDetailData] = useState(null);
  console.log("🚀 ~ PageDetail ~ detailData:", detailData);

  const fetchDetail = async () => {
    try {
      const response = await PageService.getBySlug(id);
      setDetailData(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const breadcrumbItems = [{ title: "Back to List", link: "/pages-list" }];

  const getStatusColor = (status) => {
    if (!status) return "secondary";
    const statusLower = status.toLowerCase();
    return statusLower === "draft" ? "warning" : "success";
  };

  const formatStatus = (status) => {
    if (!status) return "Unknown";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower === "published" || statusLower === "active") {
      return (
        <Badge
          color="success-subtle"
          className="text-success border border-success-subtle px-3 py-2 rounded-pill fw-bold"
        >
          Published
        </Badge>
      );
    }
    return (
      <Badge
        color="warning-subtle"
        className="text-warning border border-warning-subtle px-3 py-2 rounded-pill fw-bold"
      >
        Draft
      </Badge>
    );
  };
  const fileUrl = detailData?.slug
    ? `${detailData?.isOnshorePage ? onshoreUrl : offshoreUrl}${
        detailData?.isOnshorePage ? "/kevsintelligence" : "/other-product-services"
      }/${detailData.slug}`
    : "N/A";
  console.log("🚀 ~ PageDetail ~ fileUrl:", fileUrl);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fileUrl);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };
  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Details"
          pageTitle="Pages"
          breadcrumbItems={breadcrumbItems}
        />

        <Row className="justify-content-center">
          <Col lg={12}>
            {/* Header Card */}
            <Card className="border-0 shadow-sm mb-4">
              <CardBody className="p-4">
                <div className="d-sm-flex align-items-center justify-content-between pb-3 border-bottom border-light">
                  <div>
                    {/* Added text-primary for the Title color */}
                    <h3 className="fw-bold mb-1 text-primary text-gradient">
                      {detailData?.title || "Untitled Page"}
                    </h3>
                    <div className="d-flex align-items-center gap-3 text-muted small">
                      <span className="d-flex align-items-center gap-1">
                        <Calendar size={14} className="text-info" />
                        {detailData?.updatedAt
                          ? formatDateTime(detailData.updatedAt)
                          : "No date available"}
                      </span>
                      <span className="text-light">|</span>
                      {getStatusBadge(detailData?.status)}
                    </div>
                  </div>
                  <div className="mt-3 mt-sm-0">
                    <Button
                      color="primary"
                      className="btn-soft-primary d-flex align-items-center gap-2 shadow-sm px-4"
                      onClick={() =>
                        navigate(`/pages/update/${detailData?.slug}`)
                      }
                    >
                      <Edit3 size={16} /> Edit Page
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Row>
              {/* Left Column */}
              <Col lg={8}>
                {/* Page Content Card */}
                <Card className="border-0 border-top border-3 border-primary shadow-sm mb-4">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 fw-bold d-flex align-items-center gap-2 text-dark">
                      <FileText size={18} className="text-primary" />
                      Page Content
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4 content-body">
                    {detailData?.content ? (
                      <div
                        className="text-secondary lh-lg"
                        dangerouslySetInnerHTML={{ __html: detailData.content }}
                      />
                    ) : (
                      <p className="text-muted fst-italic mb-0 bg-light p-3 rounded text-center">
                        No content available
                      </p>
                    )}
                  </CardBody>
                </Card>

                {/* Page Information Card */}
                <Card className="border-0  shadow-sm mb-4">
                  <CardHeader className="bg-transparent border-bottom pt-4 px-4">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <FileText size={18} className="text-primary" />
                      SEO Information
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="table-responsive">
                      <table className="table table-borderless align-middle mb-0">
                        <tbody>
                          <tr>
                            <td className="ps-0 py-3" style={{ width: "50%" }}>
                              <small
                                className="text-muted d-block text-uppercase fw-bold mb-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Page Type
                              </small>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold text-dark">
                                  {detailData?.pageTypeName || "Standard Page"}
                                </span>
                                <Badge
                                  color="light"
                                  className="text-primary border border-primary-subtle fw-medium"
                                >
                                  {detailData?.pageType || "N/A"}
                                </Badge>
                              </div>
                            </td>
                            <td className="pe-0 py-3">
                              <small
                                className="text-muted d-block text-uppercase fw-bold mb-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Current Status
                              </small>
                              <Badge
                                color={getStatusColor(detailData?.status)}
                                className={`bg-${getStatusColor(
                                  detailData?.status,
                                )}-subtle text-${getStatusColor(
                                  detailData?.status,
                                )} rounded-pill px-3 py-1`}
                              >
                                {formatStatus(detailData?.status)}
                              </Badge>
                            </td>
                          </tr>

                          <tr className="border-top border-light">
                            <td className="ps-0 py-3">
                              <small
                                className="text-muted d-block text-uppercase fw-bold mb-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Created By
                              </small>
                              <div className="d-flex align-items-center gap-2 fw-medium text-dark">
                                <User size={14} className="text-primary" />{" "}
                                {detailData?.createdBy || "System Admin"}
                              </div>
                            </td>
                            <td className="pe-0 py-3">
                              <small
                                className="text-muted d-block text-uppercase fw-bold mb-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Created At
                              </small>
                              <span className="text-muted small fw-medium">
                                {detailData?.createdAt
                                  ? formatDateTime(detailData.createdAt)
                                  : "N/A"}
                              </span>
                            </td>
                          </tr>

                          <tr className="border-top border-light">
                            <td className="ps-0 py-3">
                              <small
                                className="text-muted d-block text-uppercase fw-bold mb-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Modified By
                              </small>
                              <div className="d-flex align-items-center gap-2 fw-medium text-dark">
                                <Clock size={14} className="text-primary" />
                                {detailData?.updatedBy || "No edits recorded"}
                              </div>
                            </td>
                            <td className="pe-0 py-3">
                              <small
                                className="text-muted d-block text-uppercase fw-bold mb-1"
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Last Updated
                              </small>
                              <span className="text-muted small fw-medium">
                                {detailData?.updatedAt
                                  ? formatDateTime(detailData.updatedAt)
                                  : "N/A"}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Right Column */}
              <Col lg={4}>
                {/* Featured Image Card */}
                {detailData?.featuredMedia && (
                  <Card className="border-0 shadow-sm mb-4 overflow-hidden">
                    <CardHeader className="bg-transparent border-bottom py-3">
                      <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                        <ImageIcon size={18} className="text-primary" />
                        Featured Image
                      </h5>
                    </CardHeader>
                    <CardBody className="p-0">
                      <div className="bg-light d-flex justify-content-center">
                        <img
                          src={`${imageBaseApi}${detailData?.featuredMedia?.url}`}
                          alt={detailData.featuredMedia.fileName}
                          className="img-fluid w-100"
                          style={{ maxHeight: "250px", objectFit: "contain" }}
                        />
                      </div>
                      <div className="p-3 bg-white">
                        <div className="d-flex align-items-center gap-2 text-muted small">
                          <Tag size={12} className="text-primary" />
                          <span className="text-truncate">
                            {detailData.featuredMedia.fileName}
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* SEO Information Card */}
                <Card className="border-0 shadow-sm mb-4 border-top border-primary border-3">
                  <CardHeader className="bg-transparent border-bottom py-3">
                    <h5 className="mb-0 d-flex align-items-center gap-2 text-dark">
                      <Globe size={18} className="text-primary" />
                      SEO Information
                    </h5>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="mb-3">
                      <label className="form-label fw-bold text-primary small text-uppercase mb-1">
                        Meta Title
                      </label>
                      <p className="mb-0 fw-medium text-dark border-start border-primary border-2 ps-2">
                        {detailData?.metaTitle || (
                          <span className="text-muted fst-italic">Not set</span>
                        )}
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-bold text-muted small text-uppercase mb-1">
                        Meta Description
                      </label>
                      <p className="mb-0 text-muted small bg-light p-2 rounded">
                        {detailData?.metaDescription || (
                          <span className="text-muted fst-italic">Not set</span>
                        )}
                      </p>
                    </div>

                    <div className="border-top pt-3">
                      <div className="d-flex justify-content-between align-content-center align-items-center">
                        <label className="form-label fw-bold text-muted small text-uppercase mb-2 d-flex align-items-center gap-1">
                          Url
                        </label>
                        <Button
                          size="sm"
                          color="light"
                          onClick={handleCopy}
                          className="d-flex align-items-center gap-1"
                          title="Copy"
                        >
                          <RiFileCopyLine size={16} />
                        </Button>
                      </div>

                      <div className="p-2 bg-primary-subtle rounded">
                        <code
                          className="text-primary fw-bold"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            const url = detailData?.isOnshorePage
                              ? `${onshoreUrl}/kevsintelligence/${
                                  detailData?.slug || "N/A"
                                }`
                              : `${offshoreUrl}/other-product-services/${detailData?.slug || "N/A"}`;
                            window.open(url, "_blank"); // _blank opens in new tab
                          }}
                        >
                          {detailData?.isOnshorePage
                            ? `${onshoreUrl}/kevsintelligence/${
                                detailData?.slug || "N/A"
                              }`
                            : `${offshoreUrl}/other-product-services/${detailData?.slug || "N/A"}`}
                        </code>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default PageDetail;
